import { bookingService } from './bookingService';
import { Booking } from '../types/booking';

export interface ActivityItem {
  id: string;
  type: 'booking' | 'fleet' | 'payment' | 'maintenance';
  message: string;
  timestamp: Date;
  color: string;
  iconType: 'booking' | 'payment' | 'maintenance' | 'fleet';
}

class ActivityService {
  private getCarName(car: any): string {
    if (!car) return 'Unknown Vehicle';
    const brand = car.brand || car.make || '';
    const model = car.model || '';
    const name = car.name || '';
    if (brand && model) return `${brand} ${model}`;
    if (name) return name;
    if (brand) return brand;
    return 'Unknown Vehicle';
  }

  async getRecentActivities(limit: number = 10): Promise<ActivityItem[]> {
    try {
      const now = new Date();
      const startDate = new Date(now);
      startDate.setDate(now.getDate() - 30);

      const bookingsResponse = await bookingService.getAllBookings({
        startDate,
        endDate: now,
      });

      const activities: ActivityItem[] = [];

      if (bookingsResponse.success && bookingsResponse.data) {
        const recentBookings = bookingsResponse.data
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, Math.max(limit, 15));

        recentBookings.forEach((booking: Booking) => {
          const customerName = booking.customer
            ? `${booking.customer.firstName} ${booking.customer.lastName}`
            : 'Unknown Customer';
          const carName = this.getCarName(booking.car);

          activities.push({
            id: `booking-${booking._id}`,
            type: 'booking',
            message: `New booking by ${customerName} for ${carName}`,
            timestamp: new Date(booking.createdAt),
            color: 'bg-green-400',
            iconType: 'booking',
          });

          if (booking.paymentStatus === 'paid' && booking.paidAmount > 0) {
            activities.push({
              id: `payment-${booking._id}`,
              type: 'payment',
              message: `Payment received for ${booking.bookingReference} - $${
                booking.totalAmount?.toFixed(2) || '0.00'
              }`,
              timestamp: new Date(booking.updatedAt),
              color: 'bg-blue-400',
              iconType: 'payment',
            });
          }
        });
      }

      return activities
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching activities:', error);
      return [];
    }
  }

  formatTimeAgo(date: Date): string {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

    if (seconds < 60) return `${seconds} sec ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400)
      return `${Math.floor(seconds / 3600)} hour${
        Math.floor(seconds / 3600) > 1 ? 's' : ''
      } ago`;
    if (seconds < 604800)
      return `${Math.floor(seconds / 86400)} day${
        Math.floor(seconds / 86400) > 1 ? 's' : ''
      } ago`;
    return date.toLocaleDateString();
  }
}

export const activityService = new ActivityService();
